import { fileUrl } from "@/lib/api";
import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { OrderStatusBadge } from "@/components/Common";
import { generateOrderPdf } from "@/lib/orderPdf";
import { toast } from "sonner";
import { MapPin, Image as ImageIcon, FileDown, Loader2, Trash2 } from "lucide-react";

const eur = (n) => {
  const num = Number(n || 0);
  const [i, d] = Math.abs(num).toFixed(2).split(".");
  return `${num < 0 ? "-" : ""}${i.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${d} €`;
};
const dmy = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return "—"; } };
const lineImg = (im) => (im ? (im.storage_path ? fileUrl(im.storage_path) : im.url) : null);
const addrLine = (a) => {
  if (!a) return "";
  const l2 = [a.address, a.zip, a.city, a.province ? `(${a.province})` : ""].filter(Boolean).join(" ");
  return [l2, a.country && a.country !== "Italia" ? a.country : ""].filter(Boolean).join(" · ");
};

export default function OrderDetailDialog({ order, open, onOpenChange, onDelete }) {
  const [pdfLoading, setPdfLoading] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  if (!order) return null;
  const addr = order.shipping_address || {};
  const items = order.items || [];
  const downloadPdf = async () => {
    setPdfLoading(true);
    try { await generateOrderPdf(order); }
    catch (e) { toast.error("Impossibile generare il PDF"); }
    finally { setPdfLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setConfirmDel(false); onOpenChange(o); }}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto border-border bg-brema-card" data-testid="order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-3 text-foreground">
            Ordine #{String(order._id || "").slice(-6).toUpperCase()}
            <OrderStatusBadge status={order.status} />
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" onClick={downloadPdf} disabled={pdfLoading} data-testid="download-pdf-btn"
                className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileDown className="mr-1.5 h-4 w-4" /> Scarica PDF</>}
              </Button>
              {onDelete && !confirmDel && (
                <Button size="sm" variant="danger" data-testid="order-detail-delete-btn" onClick={() => setConfirmDel(true)}
                  className="rounded-full">
                  <Trash2 className="mr-1.5 h-4 w-4" /> Elimina
                </Button>
              )}
              {onDelete && confirmDel && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">Confermi?</span>
                  <Button size="sm" data-testid="order-detail-delete-confirm-btn" onClick={() => onDelete(order._id)}
                    className="rounded-full bg-brema-red font-semibold text-white hover:bg-brema-red/90">Elimina</Button>
                  <Button size="sm" variant="secondary" onClick={() => setConfirmDel(false)}
                    className="rounded-full border border-border bg-transparent text-foreground hover:bg-brema-hover">Annulla</Button>
                </div>
              )}
            </div>
          </DialogTitle>
          <DialogDescription>Del {dmy(order.created_at)} · {order.units} pezzi</DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-border bg-brema-bg p-4">
          <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand">
            <MapPin className="h-3.5 w-3.5" /> Destinazione merce
          </div>
          {addr.recipient ? (
            <div className="text-sm text-foreground" data-testid="order-ship-address">
              <p className="font-medium text-foreground">{addr.recipient}</p>
              <p className="text-muted-foreground">{addrLine(addr)}</p>
            </div>
          ) : <p className="text-sm text-muted-foreground">Indirizzo non disponibile</p>}
        </div>

        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-brema-bg text-left text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3">Foto</th>
                <th className="p-3">Cod. articolo</th>
                <th className="p-3">Prodotto</th>
                <th className="p-3 text-center">Qtà</th>
                <th className="p-3 text-right">Prezzo listino</th>
                <th className="p-3 text-right">Sconto</th>
                <th className="p-3 text-right">Prezzo netto</th>
                <th className="p-3 text-right">Totale netto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map((it, i) => {
                const src = lineImg(it.image);
                return (
                  <tr key={i} data-testid="order-detail-row" className="text-foreground">
                    <td className="p-3">
                      <div className="h-11 w-11 overflow-hidden rounded-md bg-brema-bg">
                        {src ? <img src={src} alt="" className="h-full w-full object-cover" />
                          : <div className="flex h-full w-full items-center justify-center text-muted-foreground"><ImageIcon className="h-4 w-4" /></div>}
                      </div>
                    </td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{it.sku || "—"}</td>
                    <td className="p-3">
                      <p className="font-medium text-foreground">{it.name}</p>
                      <p className="text-xs text-muted-foreground">{[it.size && `Taglia ${it.size}`, it.color].filter(Boolean).join(" · ")}</p>
                    </td>
                    <td className="p-3 text-center">{it.quantity}</td>
                    <td className="p-3 text-right">{eur(it.list_price_excl_vat)}</td>
                    <td className="p-3 text-right text-positive">{it.discount_pct > 0 ? `-${it.discount_pct}%` : "—"}</td>
                    <td className="p-3 text-right font-medium text-foreground">{eur(it.unit_price_excl_vat)}</td>
                    <td className="p-3 text-right font-semibold text-foreground">{eur(it.line_excl_vat)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="ml-auto w-full max-w-xs space-y-1 text-sm">
          {Number(order.volume_discount_amount || 0) > 0 && (
            <>
              <div className="flex justify-between text-muted-foreground"><span>Subtotale (listino)</span><span data-testid="detail-list-subtotal">{eur(order.list_subtotal_excl_vat)}</span></div>
              <div className="flex justify-between font-medium text-positive"><span>Sconto volume −{order.volume_discount_pct}%</span><span data-testid="detail-volume-discount">−{eur(order.volume_discount_amount)}</span></div>
            </>
          )}
          <div className="flex justify-between text-foreground"><span>Imponibile</span><span data-testid="detail-subtotal">{eur(order.subtotal_excl_vat)}</span></div>
          <div className="flex justify-between text-foreground"><span>Trasporto{order.shipping_zone_label ? ` · ${order.shipping_zone_label}` : ""}</span>
            {(order.shipping_is_free || Number(order.shipping_excl_vat || 0) === 0)
              ? <span className="font-semibold text-green-600 dark:text-green-400" data-testid="detail-shipping-free">Gratuito</span>
              : <span>{eur(order.shipping_excl_vat)}</span>}
          </div>
          {(order.shipping_is_free || Number(order.shipping_excl_vat || 0) === 0) && Number(order.shipping_flat_cost || 0) > 0 && (
            <div className="flex justify-end text-xs italic text-green-600 dark:text-green-400" data-testid="detail-shipping-saved">
              Risparmi {eur(order.shipping_flat_cost)} di spedizione
            </div>
          )}
          {order.vat_exempt ? (
            <div className="flex justify-between text-foreground" data-testid="detail-vat-exempt"><span>IVA</span><span className="text-xs italic text-muted-foreground">Non imponibile</span></div>
          ) : (
            <div className="flex justify-between text-foreground"><span>IVA</span><span data-testid="detail-vat">{eur(order.vat_total)}</span></div>
          )}
          {order.vat_exempt && order.vat_exempt_reason && (
            <p className="text-[11px] leading-snug text-muted-foreground" data-testid="detail-vat-note">{order.vat_exempt_reason}</p>
          )}
          <div className="flex justify-between border-t border-border pt-2 text-foreground">
            <span className="font-semibold">{order.vat_exempt ? "Totale (IVA esente)" : "Totale (IVA inclusa)"}</span>
            <span className="text-lg font-bold" data-testid="detail-total">{eur(order.total_incl_vat ?? order.total)}</span>
          </div>
        </div>

        {order.note && <p className="rounded-lg bg-brema-bg p-3 text-sm text-muted-foreground">Nota: {order.note}</p>}
      </DialogContent>
    </Dialog>
  );
}
